import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  geoRegion?: string;
  geoPlacename?: string;
  geoPosition?: string;
  jsonLd?: object | object[];
}

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogUrl,
  geoRegion,
  geoPlacename,
  geoPosition,
  jsonLd,
}: SEOProps) {
  useEffect(() => {
    // Set title
    document.title = title;

    // Helper function to set or update meta tags
    const setMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${property}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Set basic meta tags
    setMetaTag('description', description);
    if (keywords) setMetaTag('keywords', keywords);

    // Set Open Graph tags
    setMetaTag('og:title', ogTitle || title, true);
    setMetaTag('og:description', ogDescription || description, true);
    setMetaTag('og:type', ogType, true);
    if (ogUrl) setMetaTag('og:url', ogUrl, true);

    // Set geo tags
    if (geoRegion) setMetaTag('geo.region', geoRegion);
    if (geoPlacename) setMetaTag('geo.placename', geoPlacename);
    if (geoPosition) setMetaTag('geo.position', geoPosition);

    // Set canonical link
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.setAttribute('rel', 'canonical');
        document.head.appendChild(linkElement);
      }
      
      linkElement.setAttribute('href', canonical);
    }

    // Set JSON-LD
    if (jsonLd) {
      const scriptId = 'jsonld-script';
      let scriptElement = document.getElementById(scriptId);
      
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.id = scriptId;
        scriptElement.type = 'application/ld+json';
        document.head.appendChild(scriptElement);
      }
      
      const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      scriptElement.textContent = JSON.stringify(jsonLdArray);
    }
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogType, ogUrl, geoRegion, geoPlacename, geoPosition, jsonLd]);

  return null;
}
